import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Alert, Box, Divider, FormControlLabel, Paper, Switch, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Who else may see this office's patients — the office admin's decision alone.
 * Both off by default. Sharing is read-only and covers the patient's whole visit
 * history; new visits are still recorded here. Renders nothing for non-admins.
 */
export default function PatientSharingSettings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === 'clinic_admin';

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API_URL}/api/v1/sharing/settings`).then(({ data }) => setS(data)).catch(() => {});
  }, [isAdmin]);

  if (!isAdmin || !s) return null;

  const set = async (key, value) => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${API_URL}/api/v1/sharing/settings`, { [key]: value });
      setS(data);
      toast.success(value ? 'Sharing turned on' : 'Sharing turned off');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change sharing');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(4, 47, 46, 0.12)', mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>Patient sharing</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Let others see {user?.practice_name || 'this office'}’s patients and their whole visit
        history. They can read it; only this office records new visits.
      </Typography>
      <Divider sx={{ mb: 1.5 }} />
      <Box>
        <FormControlLabel
          control={<Switch checked={s.share_patients_up} disabled={busy}
            onChange={(e) => set('share_patients_up', e.target.checked)} />}
          label="Share with the levels above" />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6, mb: 1.5 }}>
          Your region, organisation and enterprise administrators see them under Patients.
        </Typography>
        <FormControlLabel
          control={<Switch checked={s.share_patients_across} disabled={busy}
            onChange={(e) => set('share_patients_across', e.target.checked)} />}
          label="Share with other offices and regions" />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
          The organisation’s other offices and region practices see them under Patients → Shared with us.
        </Typography>
        {/* Only a region's own practice has offices beneath it to share down to, so
            nobody else is offered this. */}
        {s.can_share_down && (
          <>
            <FormControlLabel
              sx={{ mt: 1.5 }}
              control={<Switch checked={s.share_patients_down} disabled={busy}
                onChange={(e) => set('share_patients_down', e.target.checked)} />}
              label="Share with the offices in this region" />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
              The offices of this region see them under Patients → Shared with us. Narrower
              than the switch above, which reaches the whole organisation.
            </Typography>
          </>
        )}
      </Box>
      {(s.share_patients_up || s.share_patients_across || s.share_patients_down) && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Every time someone outside this office opens one of your patients, it’s recorded in the audit log.
        </Alert>
      )}
    </Paper>
  );
}
